import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { IMAGES } from "../lib/content";

export default function Census() {
  const { settings } = useSettings();
  const censusUrl = settings?.census_url;

  return (
    <>
      <PageHero
        eyebrow="Business Needs Assessment"
        lines={[
          { text: "Help us understand" },
          { text: "Kauaʻi business", className: "font-serifg italic font-semibold" },
          { text: "recovery needs" },
        ]}
        lead="Kamalama Foundation is working with HI Risk Management to understand the scope of Hurricane Lowell-related impacts on local Kauaʻi businesses."
      />

      <section className="wrap py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal className="flex flex-col items-start gap-5">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Who should complete it
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              If you are a Hawaiʻi resident who lives on Kauaʻi and owns or
              operates a locally owned business physically located on Kauaʻi
              that was affected by Hurricane Lowell or related tourism
              disruption, please complete the business-needs census.
            </p>
            <h3 className="font-display text-xl font-bold">Why it matters</h3>
            <p className="leading-relaxed text-muted-foreground">
              Your answers help the Foundation and community partners
              understand how many businesses were affected, the estimated
              scale of income disruption, and the kinds of recovery needs
              local owners face. That information shapes a fair and
              transparent recovery response.
            </p>
            <a
              className="btn btn-terra !text-base"
              href={censusUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="census-outbound-main-button"
            >
              Complete the Business Needs Census
            </a>
            <p className="text-sm text-muted-foreground">
              Opens hiriskmanagement.com/kauaibusinesses in a new tab. Nothing
              is sent from this website.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="h-full">
            <div className="flex h-full flex-col gap-4">
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={IMAGES.townStreet}
                  alt="A small-town street of locally owned businesses"
                  className="h-56 w-full object-cover transition duration-700 hover:scale-105"
                  data-testid="census-image"
                />
              </div>
              <div className="callout">
                <b className="font-display">Please read before you start</b>
                <p className="text-muted-foreground">
                  Completing the census does not guarantee funding and is{" "}
                  <b className="text-foreground">not</b> a Kamalama Foundation
                  grant application. If the Board approves a future
                  emergency-assistance program, we will share information
                  about a separate application and verification process.
                </p>
              </div>
              <div className="callout">
                <b className="font-display">Census link disclosure</b>
                <p className="text-muted-foreground">
                  The business-needs census is being conducted with HI Risk
                  Management. Please review any information, privacy terms,
                  and instructions provided through the census link before
                  submitting information. Kamalama Foundation may receive
                  aggregate information or other information permitted by the
                  census process to help assess community recovery needs and
                  plan charitable programs.
                </p>
              </div>
              <div className="callout">
                <b className="font-display">Do not send documents to us</b>
                <p className="text-muted-foreground">
                  Please don't email IDs, tax returns, leases, or financial
                  records to the Foundation. If a formal application opens, we
                  will provide a secure way to submit them.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-muted/40">
        <div className="wrap flex max-w-3xl flex-col gap-4 py-16 sm:py-20">
          <Reveal className="flex flex-col items-start gap-4">
            <p className="eyebrow">Assessment progress</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              General updates
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              Census progress is shared in our Sunday Transparency & Impact
              Update, in line with the information-sharing arrangements with
              HI Risk Management.
            </p>
            <Link
              to="/transparency"
              className="link-underline font-semibold text-primary dark:text-accent"
              data-testid="census-transparency-link"
            >
              See the latest update
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
